import React, {
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { Flex, type UseDisclosureProps, useDisclosure } from '@chakra-ui/react';

import { Viewport } from '@/components/Viewport';
import { CoachNavBar } from '@/features/coach/CoachNavBar';
import { MediaDeviceProvider } from '@/features/media';

import { CoachFooter } from './CoachFooter';

type CoachLayoutContextValue = {
  navDisplayed: boolean;
  footerDisplayed: boolean;
  setNavDisplayed: React.Dispatch<React.SetStateAction<boolean>>;
  setFooterDisplayed: React.Dispatch<React.SetStateAction<boolean>>;
  navDrawer: UseDisclosureProps;
};

export const CoachLayoutContext =
  React.createContext<CoachLayoutContextValue | null>(null);

export const useCoachLayoutContext = () => {
  const ctx = useContext(CoachLayoutContext);
  if (ctx === null) {
    throw new Error('Missing parent <CoachLayout> component');
  }
  return ctx;
};

export const useCoachLayoutHideNav = (displayed: boolean = true) => {
  const { setNavDisplayed } = useCoachLayoutContext();

  useEffect(() => {
    setNavDisplayed(displayed);
    return () => setNavDisplayed(true);
  }, [setNavDisplayed, displayed]);
};

export const useCoachLayoutHideFooter = (displayed: boolean = true) => {
  const { setFooterDisplayed } = useCoachLayoutContext();

  useLayoutEffect(() => {
    setFooterDisplayed(displayed);
    return () => setFooterDisplayed(true);
  }, [setFooterDisplayed, displayed]);
};

export const CoachLayout: FC<PropsWithChildren> = ({ children }) => {
  const [navDisplayed, setNavDisplayed] = useState<boolean>(true);
  const [footerDisplayed, setFooterDisplayed] = useState<boolean>(true);
  const navDrawer = useDisclosure();

  const providerValue = useMemo(
    () =>
      ({
        navDisplayed,
        setNavDisplayed,
        footerDisplayed,
        setFooterDisplayed,
        navDrawer,
      }) satisfies CoachLayoutContextValue,
    [footerDisplayed, navDisplayed, navDrawer]
  );

  return (
    <CoachLayoutContext.Provider value={providerValue}>
      <Viewport bgGradient="linear(to-b, brand.25, gray.50)">
        {!!navDisplayed && <CoachNavBar display="block" />}
        <Flex flex="1" direction="column">
          <MediaDeviceProvider>{children}</MediaDeviceProvider>
        </Flex>
        {!!footerDisplayed && <CoachFooter display="block" />}
      </Viewport>
    </CoachLayoutContext.Provider>
  );
};
